import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { 
  ArrowLeft,
  Send,
  FileText,
  Upload,
  X,
  File,
  Wallet,
  Wrench
} from "lucide-react";
import { format } from "date-fns";
import { ar, enUS } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTender } from "@/hooks/api/use-tenders";
import { useSubmitOffer } from "@/hooks/api/use-offers";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

const SubmitOffer = () => {
  const { t, isRTL, language } = useLanguage();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const tenderId = parseInt(id || '0');
  
  const { data: tenderData, isLoading, error } = useTender(tenderId);
  const submitOffer = useSubmitOffer();
  
  const tender = tenderData?.data;
  
  // Form state
  const [formData, setFormData] = useState({
    proposed_amount: '',
    notes: '',
  });
  const [financialFiles, setFinancialFiles] = useState<File[]>([]);
  const [technicalFiles, setTechnicalFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      closed: {
        label: t('tenders.closed') || 'مغلقة',
        className: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
      },
      active: {
        label: language === 'en' ? 'Active' : 'نشط',
        className: '',
      },
      pending: {
        label: t('tenders.pending') || 'قيد المراجعة',
        className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const handleFinancialFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'image/jpeg', 'image/jpg', 'image/png'];
    
    const invalidFiles = files.filter(file => {
      if (file.size > maxSize) {
        toast({
          title: t('common.error') || 'خطأ',
          description: `${t('offers.fileTooLarge') || 'الملف كبير جداً'}: ${file.name} (${t('offers.maxSize') || 'الحد الأقصى 10MB'})`,
          variant: 'destructive',
        });
        return true;
      }
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: t('common.error') || 'خطأ',
          description: `${t('offers.invalidFileType') || 'نوع الملف غير مدعوم'}: ${file.name}`,
          variant: 'destructive',
        });
        return true;
      }
      return false;
    });

    const validFiles = files.filter(file => !invalidFiles.includes(file));
    setFinancialFiles(prev => [...prev, ...validFiles]);
  };

  const handleTechnicalFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'image/jpeg', 'image/jpg', 'image/png'];
    
    const invalidFiles = files.filter(file => {
      if (file.size > maxSize) {
        toast({
          title: t('common.error') || 'خطأ',
          description: `${t('offers.fileTooLarge') || 'الملف كبير جداً'}: ${file.name} (${t('offers.maxSize') || 'الحد الأقصى 10MB'})`,
          variant: 'destructive',
        });
        return true;
      }
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: t('common.error') || 'خطأ',
          description: `${t('offers.invalidFileType') || 'نوع الملف غير مدعوم'}: ${file.name}`,
          variant: 'destructive',
        });
        return true;
      }
      return false;
    });

    const validFiles = files.filter(file => !invalidFiles.includes(file));
    setTechnicalFiles(prev => [...prev, ...validFiles]);
  };

  const removeFinancialFile = (index: number) => {
    setFinancialFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeTechnicalFile = (index: number) => {
    setTechnicalFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate proposed amount
    if (!formData.proposed_amount || parseFloat(formData.proposed_amount) <= 0) {
      toast({
        title: t('common.error') || 'خطأ',
        description: t('offers.amountRequired') || 'يجب إدخال مبلغ العرض',
        variant: 'destructive',
      });
      return;
    }

    // Validate that at least one document type is uploaded
    if (financialFiles.length === 0 && technicalFiles.length === 0) {
      toast({
        title: t('common.error') || 'خطأ',
        description: t('offers.documentsRequired') || 'يجب رفع مستندات مالية أو فنية على الأقل',
        variant: 'destructive',
      });
      return;
    }

    // Combine all files with their document types
    const allFiles = [
      ...financialFiles.map(f => ({ file: f, type: 'financial' as const })),
      ...technicalFiles.map(f => ({ file: f, type: 'technical' as const })),
    ];
    
    setIsSubmitting(true);
    
    try {
      await submitOffer.mutateAsync({
        tender_id: tenderId,
        proposed_amount: parseFloat(formData.proposed_amount),
        notes: formData.notes || undefined,
        attachments: allFiles.length > 0 ? allFiles.map(f => f.file) : undefined,
        document_types: allFiles.length > 0 ? allFiles.map(f => f.type) : undefined,
      });
      
      // Redirect to offers page after successful submission
      navigate('/dashboard/applications');
    } catch (error: any) {
      // Show detailed error message
      const errorMessage = error?.response?.data?.error?.message || 
                           error?.response?.data?.message || 
                           error?.message ||
                           t('offers.submitFailedDesc') || 'حدث خطأ غير متوقع';
      
      toast({
        title: t('offers.submitFailed') || 'فشل تقديم العرض',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4 mb-6">
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-10 w-24" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <Skeleton className="h-8 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-5/6" />
                  <Skeleton className="h-20 w-full" />
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="space-y-6">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !tender) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <p className="text-muted-foreground">
            {t('tenders.tenderNotFound') || 'لم يتم العثور على المناقصة'}
          </p>
          <Link to="/dashboard/tenders">
            <Button className="mt-4" variant="outline">
              <ArrowLeft className={cn("h-4 w-4", isRTL ? "ml-2" : "mr-2")} />
              {t('common.back') || 'العودة'}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Check if tender is still active
  if (tender.status !== 'active') {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <p className="text-muted-foreground">
            {t('offers.tenderNotActive') || 'هذه المناقصة غير متاحة لتقديم العروض'}
          </p>
          <Link to="/dashboard/tenders">
            <Button className="mt-4" variant="outline">
              <ArrowLeft className={cn("h-4 w-4", isRTL ? "ml-2" : "mr-2")} />
              {t('common.back') || 'العودة'}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const title = typeof tender.title === 'string' 
    ? tender.title 
    : tender.title?.[language] || tender.title?.en || '';
  const description = tender.description 
    ? (typeof tender.description === 'string' 
        ? tender.description 
        : tender.description?.[language] || tender.description?.en || '')
    : '';
  const requirements = tender.requirements
    ? (typeof tender.requirements === 'string'
        ? tender.requirements
        : tender.requirements?.[language] || tender.requirements?.en || '')
    : '';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to={`/dashboard/tenders/${tenderId}`}>
            <Button variant="outline" size="sm">
              <ArrowLeft className={cn("h-4 w-4", isRTL ? "ml-2" : "mr-2")} />
              {t('common.back') || 'العودة'}
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {t('offers.submitOffer') || 'تقديم عرض'}
            </h1>
          </div>
        </div>
        {getStatusBadge(tender.status)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tender Info & Form - 2 columns */}
        <div className="lg:col-span-2 space-y-6">
          {/* Tender Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {t('tenders.tenderDetails') || 'تفاصيل المناقصة'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {title}
                </h3>
                {description && (
                  <p className="text-muted-foreground leading-relaxed">
                    {description}
                  </p>
                )}
              </div>
              
              {requirements && (
                <div>
                  <h4 className="font-semibold text-foreground mb-2">
                    {t('tenders.requirements') || 'المتطلبات'}
                  </h4>
                  <p className="text-muted-foreground leading-relaxed">
                    {requirements}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Offer Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5" />
                {t('offers.offerDetails') || 'تفاصيل العرض'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Proposed Amount */}
                <div>
                  <Label htmlFor="proposed_amount">
                    {t('offers.proposedAmount') || 'المبلغ المقترح'} ({t('tenders.sar') || 'ريال'})
                  </Label>
                  <Input
                    id="proposed_amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.proposed_amount}
                    onChange={(e) => setFormData({ ...formData, proposed_amount: e.target.value })}
                    placeholder={t('offers.enterAmount') || 'أدخل المبلغ المقترح'}
                    required
                  />
                </div>

                {/* Notes */}
                <div>
                  <Label htmlFor="notes">
                    {t('offers.notes') || 'ملاحظات'}
                  </Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder={t('offers.enterNotes') || 'أدخل أي ملاحظات إضافية'}
                    rows={4}
                  />
                </div>

                {/* Financial Offer Documents */}
                <div>
                  <Label className="flex items-center gap-2">
                    <Wallet className="h-4 w-4" />
                    {t('offers.financialDocuments')}
                  </Label>
                  <p className="text-sm text-muted-foreground mb-2">
                    {t('offers.financialDocumentsDesc')}
                  </p>
                  <div className="mt-2">
                    <div className="flex items-center gap-2">
                      <Input
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                        onChange={handleFinancialFileChange}
                        className="hidden"
                        id="financial-file-upload"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById('financial-file-upload')?.click()}
                        className="gap-2"
                      >
                        <Upload className="h-4 w-4" />
                        {t('offers.selectFiles') || 'اختر الملفات'}
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        PDF, DOC, DOCX, XLS, XLSX, JPG, PNG
                      </span>
                    </div>
                    
                    {/* Financial Files List */}
                    {financialFiles.length > 0 && (
                      <div className="mt-4 space-y-2">
                        {financialFiles.map((file, index) => (
                          <div key={`financial-${index}`} className="flex items-center justify-between p-2 border rounded bg-green-50 dark:bg-green-900/20">
                            <div className="flex items-center gap-2">
                              <File className="h-4 w-4 text-green-600" />
                              <span className="text-sm">{file.name}</span>
                              <span className="text-xs text-muted-foreground">
                                ({(file.size / 1024 / 1024).toFixed(2)} MB)
                              </span>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeFinancialFile(index)}
                              className="h-6 w-6 p-0"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Technical Offer Documents */}
                <div>
                  <Label className="flex items-center gap-2">
                    <Wrench className="h-4 w-4" />
                    {t('offers.technicalDocuments')}
                  </Label>
                  <p className="text-sm text-muted-foreground mb-2">
                    {t('offers.technicalDocumentsDesc')}
                  </p>
                  <div className="mt-2">
                    <div className="flex items-center gap-2">
                      <Input
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                        onChange={handleTechnicalFileChange}
                        className="hidden"
                        id="technical-file-upload"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById('technical-file-upload')?.click()}
                        className="gap-2"
                      >
                        <Upload className="h-4 w-4" />
                        {t('offers.selectFiles') || 'اختر الملفات'}
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        PDF, DOC, DOCX, XLS, XLSX, JPG, PNG
                      </span>
                    </div>
                    
                    {/* Technical Files List */}
                    {technicalFiles.length > 0 && (
                      <div className="mt-4 space-y-2">
                        {technicalFiles.map((file, index) => (
                          <div key={`technical-${index}`} className="flex items-center justify-between p-2 border rounded bg-blue-50 dark:bg-blue-900/20">
                            <div className="flex items-center gap-2">
                              <File className="h-4 w-4 text-blue-600" />
                              <span className="text-sm">{file.name}</span>
                              <span className="text-xs text-muted-foreground">
                                ({(file.size / 1024 / 1024).toFixed(2)} MB)
                              </span>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeTechnicalFile(index)}
                              className="h-6 w-6 p-0"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end gap-3">
                  <Link to={`/dashboard/tenders/${tenderId}`}>
                    <Button variant="outline" type="button">
                      {t('common.cancel') || 'إلغاء'}
                    </Button>
                  </Link>
                  <Button 
                    type="submit" 
                    disabled={isSubmitting || submitOffer.isPending}
                    className="gap-2"
                  >
                    <Send className="h-4 w-4" />
                    {isSubmitting || submitOffer.isPending
                      ? (t('common.submitting') || 'جاري التقديم...')
                      : (t('offers.submitOffer') || 'تقديم العرض')
                    }
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - 1 column */}
        <div className="space-y-6">
          {/* Deadline */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {t('tenders.deadline') || 'الموعد النهائي'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {tender.deadline && (
                <div className="text-center">
                  <div className="text-lg font-bold text-primary mb-2">
                    {(() => {
                      try {
                        const date = new Date(tender.deadline);
                        if (isNaN(date.getTime())) return tender.deadline;
                        return format(date, "PPP", { locale: isRTL ? ar : enUS });
                      } catch {
                        return tender.deadline;
                      }
                    })()}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t('tenders.deadlineNote') || 'آخر موعد لتقديم العروض'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SubmitOffer;
